import { useEffect, useState } from 'react';
import {
  listDocumentTemplates,
  createDocumentTemplate,
  updateDocumentTemplate,
  cloneDocumentTemplate,
  getVariableCatalogue,
  previewDocument,
  generateDocument,
  reviewGeneratedDocument,
  signGeneratedDocument,
  issueGeneratedDocument,
  bulkGenerateDocuments,
  searchDocumentVault,
  getDocumentAnalytics,
  getDocumentDownloadUrl,
  listEmployees,
  type DocumentTemplate,
  type GeneratedDocument,
  type VariableDefinition,
  type DocumentAnalytics,
  type EmployeeRecord,
} from '../api/client';
import { Icon } from '../components/Icon';

export function DocumentCenter() {
  const [activeTab, setActiveTab] = useState<'templates' | 'studio' | 'generator' | 'approvals' | 'vault' | 'bulk'>('templates');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  
  // Data States
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [vaultDocs, setVaultDocs] = useState<GeneratedDocument[]>([]);
  const [analytics, setAnalytics] = useState<DocumentAnalytics | null>(null);
  const [variables, setVariables] = useState<VariableDefinition[]>([]);
  const [employees, setEmployees] = useState<EmployeeRecord[]>([]);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Template Studio State
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [studioName, setStudioName] = useState('');
  const [studioCategory, setStudioCategory] = useState<any>('employment');
  const [studioDocType, setStudioDocType] = useState('promotion_letter');
  const [studioDesc, setStudioDesc] = useState('');
  const [studioBody, setStudioBody] = useState('');
  const [studioHeader, setStudioHeader] = useState('');
  const [studioFooter, setStudioFooter] = useState('');
  const [studioPrefix, setStudioPrefix] = useState('DOC');
  const [studioApproval, setStudioApproval] = useState(false);

  // Generator & Live Preview State
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [customOverrideTitle, setCustomOverrideTitle] = useState('');
  const [customOverrides, setCustomOverrides] = useState<Record<string, string>>({
    'career.new_designation': '',
    'career.new_ctc': '',
    'career.effective_date': '',
    'exit.last_working_date': '',
  });
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);

  // Document Vault View Modal
  const [viewingDoc, setViewingDoc] = useState<GeneratedDocument | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Bulk Generator State
  const [bulkTemplateId, setBulkTemplateId] = useState('');
  const [bulkSelectedEmpIds, setBulkSelectedEmpIds] = useState<string[]>([]);
  const [bulkBatchName, setBulkBatchName] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  async function loadInitialData() {
    try {
      const [tRes, vRes, aRes, varRes, empRes] = await Promise.all([
        listDocumentTemplates(),
        searchDocumentVault(),
        getDocumentAnalytics().catch(() => ({ data: null })),
        getVariableCatalogue().catch(() => ({ data: [] })),
        listEmployees().catch(() => ({ items: [] })),
      ]);

      setTemplates(tRes.data || []);
      setVaultDocs(vRes.data || []);
      setAnalytics(aRes.data);
      setVariables(varRes.data || []);
      setEmployees(empRes.items || []);

      if (tRes.data?.length > 0 && !selectedTemplateId) {
        setSelectedTemplateId(tRes.data[0].id);
        setBulkTemplateId(tRes.data[0].id);
      }
    } catch (err: any) {
      showToast('error', err.message || 'Failed to load document engine data.');
    }
  }

  function showToast(type: 'success' | 'error', message: string) {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4500);
  }

  // Update Live Preview whenever generator inputs change
  useEffect(() => {
    if (activeTab === 'generator' && selectedTemplateId) {
      triggerPreview();
    }
  }, [activeTab, selectedTemplateId, selectedEmployeeId, customOverrides]);

  async function triggerPreview() {
    try {
      setPreviewLoading(true);
      const activeOverrides: Record<string, any> = {};
      for (const [k, v] of Object.entries(customOverrides)) {
        if (v.trim()) activeOverrides[k] = v.trim();
      }

      const res = await previewDocument({
        templateId: selectedTemplateId,
        employeeId: selectedEmployeeId || null,
        customVariables: activeOverrides,
      });

      setPreviewHtml(res.data.renderedHtml);
    } catch (err) {
      console.error('Preview error', err);
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleSaveTemplate() {
    try {
      if (!studioName.trim() || !studioBody.trim()) {
        showToast('error', 'Please provide a template title and body content.');
        return;
      }

      if (editingTemplateId) {
        await updateDocumentTemplate(editingTemplateId, {
          name: studioName,
          category: studioCategory,
          document_type: studioDocType,
          description: studioDesc,
          template_body: studioBody,
          header_html: studioHeader || null,
          footer_html: studioFooter || null,
          numbering_prefix: studioPrefix,
          requires_approval: studioApproval ? 1 : 0,
          incrementVersion: true,
        });
        showToast('success', 'Template updated and new version incremented!');
      } else {
        await createDocumentTemplate({
          name: studioName,
          category: studioCategory,
          documentType: studioDocType,
          description: studioDesc,
          templateBody: studioBody,
          headerHtml: studioHeader || undefined,
          footerHtml: studioFooter || undefined,
          numberingPrefix: studioPrefix,
          requiresApproval: studioApproval,
        });
        showToast('success', 'New universal document template created!');
      }

      setEditingTemplateId(null);
      loadInitialData();
      setActiveTab('templates');
    } catch (err: any) {
      showToast('error', err.message || 'Failed to save template.');
    }
  }

  function handleEditTemplate(t: DocumentTemplate) {
    setEditingTemplateId(t.id);
    setStudioName(t.name);
    setStudioCategory(t.category);
    setStudioDocType(t.document_type);
    setStudioDesc(t.description || '');
    setStudioBody(t.template_body);
    setStudioHeader(t.header_html || '');
    setStudioFooter(t.footer_html || '');
    setStudioPrefix(t.numbering_prefix);
    setStudioApproval(Boolean(t.requires_approval));
    setActiveTab('studio');
  }

  async function handleCloneTemplate(t: DocumentTemplate) {
    try {
      await cloneDocumentTemplate(t.id, `${t.name} (Copy)`);
      showToast('success', `Cloned template "${t.name}" successfully!`);
      loadInitialData();
    } catch (err: any) {
      showToast('error', err.message || 'Failed to clone template.');
    }
  }

  async function handleGenerateOfficial() {
    try {
      if (!selectedTemplateId) {
        showToast('error', 'Select a document template to generate.');
        return;
      }

      const activeOverrides: Record<string, any> = {};
      for (const [k, v] of Object.entries(customOverrides)) {
        if (v.trim()) activeOverrides[k] = v.trim();
      }

      const res = await generateDocument({
        templateId: selectedTemplateId,
        employeeId: selectedEmployeeId || null,
        title: customOverrideTitle || undefined,
        customVariables: activeOverrides,
      });

      showToast('success', `Generated official document ${res.data.document_number} with status "${res.data.status}"!`);
      loadInitialData();
      setActiveTab('vault');
    } catch (err: any) {
      showToast('error', err.message || 'Document generation failed.');
    }
  }

  async function handleApproveDoc(doc: GeneratedDocument, status: 'approved' | 'rejected') {
    try {
      const remarks = prompt(status === 'approved' ? 'Approval remarks (optional):' : 'Reason for rejection:');
      if (status === 'rejected' && !remarks) return;

      await reviewGeneratedDocument(doc.id, { status, remarks: remarks || undefined });
      showToast('success', `Document ${doc.document_number} marked as ${status}!`);
      loadInitialData();
    } catch (err: any) {
      showToast('error', err.message || 'Action failed.');
    }
  }

  async function handleSignDoc(doc: GeneratedDocument) {
    try {
      const signerRole = prompt('Enter your Signatory Role:', 'Authorized Executive') || 'Authorized Executive';
      await signGeneratedDocument(doc.id, { signerRole });
      showToast('success', `Document ${doc.document_number} digitally signed!`);
      loadInitialData();
    } catch (err: any) {
      showToast('error', err.message || 'Signing failed.');
    }
  }

  async function handleIssueDoc(doc: GeneratedDocument) {
    try {
      await issueGeneratedDocument(doc.id);
      showToast('success', `Document ${doc.document_number} officially issued to employee!`);
      loadInitialData();
    } catch (err: any) {
      showToast('error', err.message || 'Issuance failed.');
    }
  }

  async function handleBulkSubmit() {
    try {
      if (!bulkTemplateId || bulkSelectedEmpIds.length === 0) {
        showToast('error', 'Please select a template and at least one employee.');
        return;
      }
      setBulkProcessing(true);
      const res = await bulkGenerateDocuments({
        templateId: bulkTemplateId,
        employeeIds: bulkSelectedEmpIds,
        batchName: bulkBatchName || undefined,
      });
      showToast('success', `Bulk batch initiated! Generated ${res.data.successes} of ${res.data.total} letters.`);
      loadInitialData();
      setActiveTab('vault');
    } catch (err: any) {
      showToast('error', err.message || 'Bulk generation failed.');
    } finally {
      setBulkProcessing(false);
    }
  }

  async function handleDownloadDoc(docId: string) {
    try {
      const res = await getDocumentDownloadUrl(docId);
      window.open(res.downloadUrl, '_blank');
    } catch (err: any) {
      showToast('error', err.message || 'Download link failed.');
    }
  }

  const filteredTemplates = templates.filter((t) => {
    if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
    return true;
  });

  const pendingApprovals = vaultDocs.filter((d) => d.status === 'pending_approval' || d.status === 'pending_review');

  const filteredVault = vaultDocs.filter((d) => {
    if (categoryFilter !== 'all' && d.category !== categoryFilter) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const num = (d.document_number || '').toLowerCase();
      const title = (d.title || '').toLowerCase();
      const emp = `${d.employee_first_name || ''} ${d.employee_last_name || ''}`.toLowerCase();
      return num.includes(q) || title.includes(q) || emp.includes(q);
    }
    return true;
  });

  return (
    <div style={{ padding: '24px 32px', maxWidth: '1440px', margin: '0 auto' }}>
      {/* Toast Notification */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 9999,
            padding: '12px 20px',
            borderRadius: '10px',
            backgroundColor: toast.type === 'success' ? '#065f46' : '#991b1b',
            color: '#fff',
            boxShadow: '0 10px 25px rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          <Icon name={toast.type === 'success' ? 'check' : 'alertCircle'} size={18} />
          {toast.message}
        </div>
      )}

      {/* Header Banner */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
              Universal HR Document & Letter Engine
            </h1>
            <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px', background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)' }}>
              v3.7 Enterprise
            </span>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: '6px 0 0 0' }}>
            Centralized dynamic template builder, live data snapshot freeze, approval governance, digital signatures & employee document vault.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => {
              setEditingTemplateId(null);
              setStudioName('');
              setStudioDesc('');
              setStudioBody('<p>Dear <strong>{{employee.name}}</strong>,</p>\n<p>Body content here...</p>');
              setActiveTab('studio');
            }}
            className="btn btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
          >
            <Icon name="plus" size={16} /> New Template
          </button>
          <button
            onClick={() => setActiveTab('generator')}
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
          >
            <Icon name="fileText" size={16} /> Generate Letter
          </button>
        </div>
      </div>

      {/* Analytics KPI Bar */}
      {analytics && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div className="card" style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'rgba(99,102,241,0.15)', color: '#818cf8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="fileText" size={20} />
            </div>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Total Documents Generated</div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)' }}>{analytics.totalGenerated}</div>
            </div>
          </div>

          <div className="card" style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'rgba(245,158,11,0.15)', color: '#fbbf24', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="clock" size={20} />
            </div>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Pending Review / Approval</div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#fbbf24' }}>{pendingApprovals.length}</div>
            </div>
          </div>

          <div className="card" style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'rgba(16,185,129,0.15)', color: '#34d399', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="checkCircle" size={20} />
            </div>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Officially Issued & Signed</div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#34d399' }}>{analytics.issued}</div>
            </div>
          </div>

          <div className="card" style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'rgba(139,92,246,0.15)', color: '#a78bfa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="layers" size={20} />
            </div>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Active Master Templates</div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)' }}>{analytics.activeTemplates}</div>
            </div>
          </div>
        </div>
      )}

      {/* Main Tabs Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[
            { id: 'templates', label: 'Template Library', icon: 'layers' },
            { id: 'generator', label: 'Live Generator & Preview', icon: 'fileText' },
            { id: 'approvals', label: `Approval Queue (${pendingApprovals.length})`, icon: 'checkCircle' },
            { id: 'vault', label: 'Document Vault & Audit', icon: 'lock' },
            { id: 'bulk', label: 'Bulk Mass Issuance', icon: 'users' },
            { id: 'studio', label: editingTemplateId ? 'Template Studio (Edit)' : 'Template Studio', icon: 'edit' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                padding: '12px 18px',
                border: 'none',
                background: 'transparent',
                fontSize: '13.5px',
                fontWeight: activeTab === tab.id ? 600 : 400,
                color: activeTab === tab.id ? 'var(--brand-primary, #6366f1)' : 'var(--text-secondary)',
                borderBottom: activeTab === tab.id ? '2px solid var(--brand-primary, #6366f1)' : '2px solid transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Icon name={tab.icon as any} size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Category Filter Pills (when browsing templates or vault) */}
        {(activeTab === 'templates' || activeTab === 'vault') && (
          <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '6px' }}>
            {['all', 'recruitment', 'joining', 'employment', 'performance', 'payroll', 'employee', 'exit', 'gratuity'].map((c) => (
              <button
                key={c}
                onClick={() => setCategoryFilter(c)}
                style={{
                  fontSize: '11px',
                  fontWeight: 500,
                  textTransform: 'capitalize',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  border: '1px solid',
                  borderColor: categoryFilter === c ? 'var(--brand-primary, #6366f1)' : 'var(--border-color)',
                  background: categoryFilter === c ? 'rgba(99,102,241,0.15)' : 'transparent',
                  color: categoryFilter === c ? '#818cf8' : 'var(--text-secondary)',
                  cursor: 'pointer',
                }}
              >
                {c}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* TAB 1: TEMPLATE LIBRARY */}
      {activeTab === 'templates' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
            {filteredTemplates.map((t) => (
              <div key={t.id} className="card" style={{ padding: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <span style={{ fontSize: '10.5px', fontWeight: 600, textTransform: 'uppercase', padding: '2px 8px', borderRadius: '4px', background: 'rgba(99,102,241,0.15)', color: '#818cf8' }}>
                      {t.category}
                    </span>
                    <span style={{ fontSize: '11px', fontFamily: 'monospace', color: 'var(--text-secondary)' }}>
                      {t.version}
                    </span>
                  </div>

                  <h3 style={{ fontSize: '15.5px', fontWeight: 600, margin: '0 0 6px 0', color: 'var(--text-primary)' }}>
                    {t.name}
                  </h3>
                  <p style={{ fontSize: '12.5px', color: 'var(--text-secondary)', margin: '0 0 16px 0', lineHeight: 1.4, minHeight: '35px' }}>
                    {t.description || 'Enterprise document template with configurable variables and rules.'}
                  </p>

                  <div style={{ display: 'flex', gap: '8px', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                    <span>Prefix: <strong>{t.numbering_prefix}</strong></span>
                    <span>•</span>
                    <span>Approval: <strong>{t.requires_approval ? 'Required' : 'Auto'}</strong></span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid var(--border-color)', paddingTop: '14px' }}>
                  <button
                    onClick={() => {
                      setSelectedTemplateId(t.id);
                      setActiveTab('generator');
                    }}
                    className="btn btn-primary"
                    style={{ flex: 1, fontSize: '12px', padding: '6px 12px' }}
                  >
                    Generate
                  </button>
                  <button
                    onClick={() => handleEditTemplate(t)}
                    className="btn btn-secondary"
                    style={{ fontSize: '12px', padding: '6px 10px' }}
                    title="Edit in Template Studio"
                  >
                    <Icon name="edit" size={14} />
                  </button>
                  <button
                    onClick={() => handleCloneTemplate(t)}
                    className="btn btn-secondary"
                    style={{ fontSize: '12px', padding: '6px 10px' }}
                    title="Duplicate / Clone"
                  >
                    <Icon name="layers" size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 2: LIVE GENERATOR & PREVIEW */}
      {activeTab === 'generator' && (
        <div style={{ display: 'grid', gridTemplateColumns: '420px 1fr', gap: '24px' }}>
          {/* Left Panel: Inputs & Variable Selectors */}
          <div className="card" style={{ padding: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 16px 0', color: 'var(--text-primary)' }}>
              Document Parameters
            </h3>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                Select Master Template
              </label>
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input, #1e293b)', color: '#fff', fontSize: '13px' }}
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    [{t.category.toUpperCase()}] {t.name} ({t.version})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                Target Employee (Auto-populate)
              </label>
              <select
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input, #1e293b)', color: '#fff', fontSize: '13px' }}
              >
                <option value="">-- Generic Sample Data --</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.first_name} {e.last_name} ({e.employee_code}) - {e.designation || 'Staff'}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                Document Title Override (Optional)
              </label>
              <input
                type="text"
                value={customOverrideTitle}
                onChange={(e) => setCustomOverrideTitle(e.target.value)}
                placeholder="e.g. Promotion to Principal Architect"
                style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input, #1e293b)', color: '#fff', fontSize: '13px' }}
              />
            </div>

            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '12px', marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>
                Contextual Variable Overrides
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                <div>
                  <label style={{ fontSize: '10.5px', color: 'var(--text-secondary)' }}>New Designation</label>
                  <input
                    type="text"
                    value={customOverrides['career.new_designation']}
                    onChange={(e) => setCustomOverrides({ ...customOverrides, 'career.new_designation': e.target.value })}
                    placeholder="e.g. Director of Eng"
                    style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-input, #1e293b)', color: '#fff', fontSize: '12px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '10.5px', color: 'var(--text-secondary)' }}>Revised CTC (Annual)</label>
                  <input
                    type="text"
                    value={customOverrides['career.new_ctc']}
                    onChange={(e) => setCustomOverrides({ ...customOverrides, 'career.new_ctc': e.target.value })}
                    placeholder="e.g. ₹24,00,000"
                    style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-input, #1e293b)', color: '#fff', fontSize: '12px' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={{ fontSize: '10.5px', color: 'var(--text-secondary)' }}>Effective Transition Date</label>
                  <input
                    type="date"
                    value={customOverrides['career.effective_date']}
                    onChange={(e) => setCustomOverrides({ ...customOverrides, 'career.effective_date': e.target.value })}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-input, #1e293b)', color: '#fff', fontSize: '12px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '10.5px', color: 'var(--text-secondary)' }}>Last Working Date (Exit)</label>
                  <input
                    type="date"
                    value={customOverrides['exit.last_working_date']}
                    onChange={(e) => setCustomOverrides({ ...customOverrides, 'exit.last_working_date': e.target.value })}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-input, #1e293b)', color: '#fff', fontSize: '12px' }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={handleGenerateOfficial}
                className="btn btn-primary"
                style={{ padding: '10px 16px', fontSize: '13.5px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                <Icon name="check" size={16} />
                Generate & Freeze Official Record
              </button>
            </div>
          </div>

          {/* Right Panel: Live Document Renderer */}
          <div className="card" style={{ padding: '0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border-color)', background: 'rgba(255,255,255,0.02)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: previewLoading ? '#f59e0b' : '#10b981' }} />
                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Live Document Render (Print Preview)
                </span>
              </div>
              <button
                onClick={() => triggerPreview()}
                className="btn btn-secondary"
                style={{ padding: '4px 8px', fontSize: '11px' }}
              >
                Refresh Render
              </button>
            </div>

            <div style={{ flex: 1, minHeight: '600px', background: '#fff', padding: '12px', overflowY: 'auto' }}>
              <iframe
                title="Document Preview"
                srcDoc={previewHtml}
                style={{ width: '100%', height: '700px', border: 'none', background: '#fff' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: APPROVAL QUEUE */}
      {activeTab === 'approvals' && (
        <div className="card" style={{ padding: '20px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 16px 0', color: 'var(--text-primary)' }}>
            Document Governance & Approval Queue
          </h3>

          {pendingApprovals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
              <Icon name="checkCircle" size={40} />
              <p style={{ marginTop: '12px', fontSize: '14px' }}>All generated documents have been reviewed and approved!</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)', textAlign: 'left', color: 'var(--text-secondary)' }}>
                  <th style={{ padding: '10px 12px' }}>Document Ref</th>
                  <th style={{ padding: '10px 12px' }}>Title & Category</th>
                  <th style={{ padding: '10px 12px' }}>Employee</th>
                  <th style={{ padding: '10px 12px' }}>Date Generated</th>
                  <th style={{ padding: '10px 12px' }}>Status</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingApprovals.map((d) => (
                  <tr key={d.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '12px', fontFamily: 'monospace', fontWeight: 600 }}>{d.document_number}</td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ fontWeight: 600 }}>{d.title}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{d.category}</div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {d.employee_first_name ? `${d.employee_first_name} ${d.employee_last_name} (${d.employee_code})` : '—'}
                    </td>
                    <td style={{ padding: '12px', color: 'var(--text-secondary)' }}>
                      {new Date(d.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '4px', background: 'rgba(245,158,11,0.15)', color: '#fbbf24' }}>
                        {d.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <button
                          onClick={() => setViewingDoc(d)}
                          className="btn btn-secondary"
                          style={{ padding: '4px 8px', fontSize: '11.5px' }}
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleSignDoc(d)}
                          className="btn btn-secondary"
                          style={{ padding: '4px 8px', fontSize: '11.5px', color: '#818cf8' }}
                        >
                          Sign
                        </button>
                        <button
                          onClick={() => handleApproveDoc(d, 'approved')}
                          className="btn btn-primary"
                          style={{ padding: '4px 10px', fontSize: '11.5px' }}
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleApproveDoc(d, 'rejected')}
                          className="btn btn-secondary"
                          style={{ padding: '4px 8px', fontSize: '11.5px', color: '#f87171' }}
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* TAB 4: ENTERPRISE DOCUMENT VAULT */}
      {activeTab === 'vault' && (
        <div className="card" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>
                Official Document Vault
              </h3>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '4px 0 0 0' }}>
                Immutable historical records, signed documents and verified credentials.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search reference, employee, title..."
                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input, #1e293b)', color: '#fff', fontSize: '12.5px', width: '260px' }}
              />
            </div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', textAlign: 'left', color: 'var(--text-secondary)' }}>
                <th style={{ padding: '10px 12px' }}>Document Ref</th>
                <th style={{ padding: '10px 12px' }}>Title & Category</th>
                <th style={{ padding: '10px 12px' }}>Recipient</th>
                <th style={{ padding: '10px 12px' }}>Version</th>
                <th style={{ padding: '10px 12px' }}>Status</th>
                <th style={{ padding: '10px 12px' }}>Date</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVault.map((d) => (
                <tr key={d.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ padding: '12px', fontFamily: 'monospace', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {d.document_number}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{d.title}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{d.category}</div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    {d.employee_first_name ? `${d.employee_first_name} ${d.employee_last_name}` : '—'}
                  </td>
                  <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '11px' }}>{d.template_version}</td>
                  <td style={{ padding: '12px' }}>
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '3px 8px',
                        borderRadius: '4px',
                        background:
                          d.status === 'issued' || d.status === 'generated'
                            ? 'rgba(16,185,129,0.15)'
                            : d.status === 'approved'
                            ? 'rgba(99,102,241,0.15)'
                            : d.status === 'rejected'
                            ? 'rgba(239,68,68,0.15)'
                            : 'rgba(245,158,11,0.15)',
                        color:
                          d.status === 'issued' || d.status === 'generated'
                            ? '#34d399'
                            : d.status === 'approved'
                            ? '#818cf8'
                            : d.status === 'rejected'
                            ? '#f87171'
                            : '#fbbf24',
                      }}
                    >
                      {d.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px', color: 'var(--text-secondary)' }}>
                    {new Date(d.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                      <button
                        onClick={() => setViewingDoc(d)}
                        className="btn btn-secondary"
                        style={{ padding: '4px 8px', fontSize: '11.5px' }}
                      >
                        Preview
                      </button>
                      {d.status === 'approved' && (
                        <button
                          onClick={() => handleIssueDoc(d)}
                          className="btn btn-primary"
                          style={{ padding: '4px 8px', fontSize: '11.5px' }}
                        >
                          Issue
                        </button>
                      )}
                      <button
                        onClick={() => handleDownloadDoc(d.id)}
                        className="btn btn-secondary"
                        style={{ padding: '4px 8px', fontSize: '11.5px', color: '#38bdf8' }}
                        title="Download Document"
                      >
                        <Icon name="download" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB 5: BULK MASS GENERATOR */}
      {activeTab === 'bulk' && (
        <div className="card" style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 8px 0', color: 'var(--text-primary)' }}>
            Bulk Mass Letter Generation Wizard
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 20px 0' }}>
            Generate individual letters (e.g. Annual Compensation Statements, Increment Letters, Policy Acknowledgements) asynchronously across multiple employees.
          </p>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '12.5px', fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
              Select Master Template
            </label>
            <select
              value={bulkTemplateId}
              onChange={(e) => setBulkTemplateId(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input, #1e293b)', color: '#fff', fontSize: '13px' }}
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  [{t.category.toUpperCase()}] {t.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '12.5px', fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
              Batch Name / Description
            </label>
            <input
              type="text"
              value={bulkBatchName}
              onChange={(e) => setBulkBatchName(e.target.value)}
              placeholder="e.g. FY27 Annual Salary Increment Letters"
              style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input, #1e293b)', color: '#fff', fontSize: '13px' }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ fontSize: '12.5px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                Target Recipients ({bulkSelectedEmpIds.length} selected)
              </label>
              <button
                type="button"
                onClick={() => {
                  if (bulkSelectedEmpIds.length === employees.length) {
                    setBulkSelectedEmpIds([]);
                  } else {
                    setBulkSelectedEmpIds(employees.map((e) => e.id));
                  }
                }}
                style={{ fontSize: '11px', color: '#818cf8', background: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                {bulkSelectedEmpIds.length === employees.length ? 'Deselect All' : 'Select All Employees'}
              </button>
            </div>

            <div style={{ maxHeight: '240px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '8px' }}>
              {employees.map((e) => (
                <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 8px', fontSize: '13px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                  <input
                    type="checkbox"
                    checked={bulkSelectedEmpIds.includes(e.id)}
                    onChange={(evt) => {
                      if (evt.target.checked) {
                        setBulkSelectedEmpIds([...bulkSelectedEmpIds, e.id]);
                      } else {
                        setBulkSelectedEmpIds(bulkSelectedEmpIds.filter((id) => id !== e.id));
                      }
                    }}
                  />
                  <span>
                    <strong>{e.first_name} {e.last_name}</strong> ({e.employee_code}) — {e.designation || 'Staff'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <button
            onClick={handleBulkSubmit}
            disabled={bulkProcessing || bulkSelectedEmpIds.length === 0}
            className="btn btn-primary"
            style={{ width: '100%', padding: '12px', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
          >
            <Icon name="users" size={18} />
            {bulkProcessing ? 'Processing Batch...' : `Execute Bulk Issuance (${bulkSelectedEmpIds.length} Letters)`}
          </button>
        </div>
      )}

      {/* TAB 6: TEMPLATE STUDIO / BUILDER */}
      {activeTab === 'studio' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '24px' }}>
          {/* Main Editor */}
          <div className="card" style={{ padding: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 16px 0', color: 'var(--text-primary)' }}>
              {editingTemplateId ? `Edit Template: ${studioName}` : 'Create Universal Document Template'}
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', marginBottom: '14px' }}>
              <div>
                <label style={{ fontSize: '11.5px', color: 'var(--text-secondary)' }}>Template Name</label>
                <input
                  type="text"
                  value={studioName}
                  onChange={(e) => setStudioName(e.target.value)}
                  placeholder="e.g. Standard Appointment Letter"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input, #1e293b)', color: '#fff', fontSize: '13px' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '11.5px', color: 'var(--text-secondary)' }}>Category</label>
                <select
                  value={studioCategory}
                  onChange={(e) => setStudioCategory(e.target.value as any)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input, #1e293b)', color: '#fff', fontSize: '13px' }}
                >
                  <option value="recruitment">Recruitment</option>
                  <option value="joining">Joining</option>
                  <option value="employment">Employment</option>
                  <option value="performance">Performance</option>
                  <option value="payroll">Payroll</option>
                  <option value="employee">Employee</option>
                  <option value="exit">Exit</option>
                  <option value="gratuity">Gratuity</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '11.5px', color: 'var(--text-secondary)' }}>Serial Prefix</label>
                <input
                  type="text"
                  value={studioPrefix}
                  onChange={(e) => setStudioPrefix(e.target.value)}
                  placeholder="e.g. OFFER"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input, #1e293b)', color: '#fff', fontSize: '13px' }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '11.5px', color: 'var(--text-secondary)' }}>Description & Purpose</label>
              <input
                type="text"
                value={studioDesc}
                onChange={(e) => setStudioDesc(e.target.value)}
                placeholder="Brief description of the letter purpose"
                style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input, #1e293b)', color: '#fff', fontSize: '13px' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                  Template HTML Body (Supports variables & conditionals)
                </label>
                <span style={{ fontSize: '11px', color: '#818cf8' }}>Use tags like &#123;&#123;employee.name&#125;&#125;</span>
              </div>
              <textarea
                value={studioBody}
                onChange={(e) => setStudioBody(e.target.value)}
                rows={14}
                style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: '#0f172a', color: '#f8fafc', fontFamily: 'monospace', fontSize: '13px', lineHeight: 1.5 }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={studioApproval}
                  onChange={(e) => setStudioApproval(e.target.checked)}
                />
                Requires Management / Reviewer Approval Before Issuance
              </label>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setActiveTab('templates')}
                  className="btn btn-secondary"
                  style={{ fontSize: '13px' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveTemplate}
                  className="btn btn-primary"
                  style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <Icon name="check" size={16} />
                  {editingTemplateId ? 'Save & Increment Version' : 'Publish Master Template'}
                </button>
              </div>
            </div>
          </div>

          {/* Right Drawer: Variable Picker */}
          <div className="card" style={{ padding: '16px', maxHeight: '720px', overflowY: 'auto' }}>
            <h4 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 10px 0', color: 'var(--text-primary)' }}>
              Variable Catalogue
            </h4>
            <p style={{ fontSize: '11.5px', color: 'var(--text-secondary)', margin: '0 0 12px 0' }}>
              Click any variable to append it to the template body:
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {variables.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => setStudioBody(studioBody + ` {{${v.key}}}`)}
                  style={{
                    textAlign: 'left',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)',
                    background: 'rgba(255,255,255,0.02)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: '11.5px',
                  }}
                  title={v.description}
                >
                  <div style={{ fontFamily: 'monospace', color: '#818cf8', fontWeight: 600 }}>
                    &#123;&#123;{v.key}&#125;&#125;
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '10.5px' }}>{v.label}</div>
                </button>
              ))}
            </div>

            <div style={{ marginTop: '16px', borderTop: '1px solid var(--border-color)', paddingTop: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: 'var(--text-secondary)' }}>
                Conditional Blocks
              </div>
              <button
                type="button"
                onClick={() => setStudioBody(studioBody + '\n{% if employee.employment_type == "permanent" %}\n<p>Permanent clause...</p>\n{% else %}\n<p>Contract clause...</p>\n{% endif %}')}
                className="btn btn-secondary"
                style={{ width: '100%', fontSize: '11px', padding: '6px', textAlign: 'left' }}
              >
                + Insert IF Employment Type Block
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DOCUMENT PREVIEW MODAL */}
      {viewingDoc && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div className="card" style={{ maxWidth: '900px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: '#0b0f19' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>
                  {viewingDoc.title} ({viewingDoc.document_number})
                </h3>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                  Status: <strong>{viewingDoc.status}</strong> • Version: {viewingDoc.template_version}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => handleDownloadDoc(viewingDoc.id)}
                  className="btn btn-primary"
                  style={{ padding: '6px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <Icon name="download" size={14} /> Download HTML / Print
                </button>
                <button
                  onClick={() => setViewingDoc(null)}
                  className="btn btn-secondary"
                  style={{ padding: '6px 10px', fontSize: '12px' }}
                >
                  Close
                </button>
              </div>
            </div>

            <div style={{ flex: 1, padding: '16px', background: '#fff', overflowY: 'auto' }}>
              <iframe
                title="Document View"
                srcDoc={viewingDoc.rendered_content_html}
                style={{ width: '100%', height: '600px', border: 'none', background: '#fff' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
